import type { MfaMethod } from '@crm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError } from '../lib/api';
import { MfaChallenge } from './Login';

/** Tokens arrive in the URL fragment so they never reach server logs or Referer headers. */
const fragmentToken = () => window.location.hash.replace(/^#/, '');

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setFieldErrors({});
    try { await fn(); } catch (e) {
      if (e instanceof ApiError) { setError(e.message); setFieldErrors(e.fieldErrors()); } else setError('Something went wrong. Try again.');
    } finally { setBusy(false); }
  };
  return { busy, error, fieldErrors, run };
}

export function AcceptInvite() {
  const qc = useQueryClient();
  const token = fragmentToken();
  const s = useSubmit();
  const [challenge, setChallenge] = useState<{ challenge: string; methods?: MfaMethod[] } | null>(null);
  const peek = useQuery({
    queryKey: ['invite-peek', token],
    retry: false,
    queryFn: () => api<{ email: string; organizationName: string; hasAccount: boolean }>('/api/auth/invitations/peek', { method: 'POST', body: { token } }),
  });

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = { token, ...Object.fromEntries(new FormData(e.currentTarget).entries()) };
    s.run(async () => {
      const r = await api<{ mfaRequired?: boolean; challenge?: string; methods?: MfaMethod[] }>('/api/auth/invitations/accept', { method: 'POST', body });
      history.replaceState(null, '', '/');
      if (r?.mfaRequired && r.challenge) { setChallenge({ challenge: r.challenge, methods: r.methods }); return; }
      await qc.invalidateQueries({ queryKey: ['session'] });
    });
  }

  if (challenge) return <MfaChallenge challenge={challenge.challenge} methods={challenge.methods} onRestart={() => (window.location.href = '/login')} />;
  if (peek.isPending) return <main className="auth"><p className="muted">Checking your invitation…</p></main>;
  if (peek.isError) {
    return (
      <main className="auth"><div className="auth-card"><h1>Invitation unavailable</h1><p>{(peek.error as Error).message}</p><Link to="/login">Go to sign in</Link></div></main>
    );
  }
  const { email, organizationName, hasAccount } = peek.data;
  return (
    <main className="auth">
      <form className="auth-card" onSubmit={submit} noValidate>
        <h1>Join {organizationName}</h1>
        <p className="muted">Invitation for <b>{email}</b>.</p>
        {!hasAccount && (
          <div className="field">
            <label htmlFor="ai-name">Your name</label>
            <input id="ai-name" name="name" autoComplete="name" required />
            {s.fieldErrors.name && <span className="field-error">{s.fieldErrors.name}</span>}
          </div>
        )}
        <div className="field">
          <label htmlFor="ai-pw">{hasAccount ? 'Your existing password' : 'Choose a password'}</label>
          <input id="ai-pw" name="password" type="password" autoComplete={hasAccount ? 'current-password' : 'new-password'} required />
          {s.fieldErrors.password && <span className="field-error">{s.fieldErrors.password}</span>}
          {!hasAccount && <span className="hint">At least 8 characters: any letters, numbers or symbols you like.</span>}
        </div>
        {s.error && <p className="form-error" role="alert">{s.error}</p>}
        <button className="btn primary" disabled={s.busy}>{s.busy ? 'Joining…' : 'Accept invitation'}</button>
      </form>
    </main>
  );
}

export function ForgotPassword() {
  const s = useSubmit();
  const [sent, setSent] = useState(false);
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get('email'));
    s.run(async () => { await api('/api/auth/password-reset/request', { method: 'POST', body: { email } }); setSent(true); });
  }
  return (
    <main className="auth">
      <form className="auth-card" onSubmit={submit} noValidate>
        <h1>Reset your password</h1>
        {sent ? (
          <p>If an account exists for that email, a reset link is on its way. It's valid for one hour.</p>
        ) : (
          <>
            <div className="field"><label htmlFor="fp-email">Email</label><input id="fp-email" name="email" type="email" autoComplete="email" required /></div>
            {s.error && <p className="form-error" role="alert">{s.error}</p>}
            <button className="btn primary" disabled={s.busy}>Send reset link</button>
          </>
        )}
        <p className="hint"><Link to="/login">Back to sign in</Link></p>
      </form>
    </main>
  );
}

export function ResetPassword() {
  const s = useSubmit();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const password = String(new FormData(e.currentTarget).get('password'));
    s.run(async () => {
      await api('/api/auth/password-reset/confirm', { method: 'POST', body: { token: fragmentToken(), password } });
      history.replaceState(null, '', '/reset-password');
      setDone(true);
    });
  }
  return (
    <main className="auth">
      <form className="auth-card" onSubmit={submit} noValidate>
        <h1>Choose a new password</h1>
        {done ? (
          <>
            <p>Your password was changed and you've been signed out everywhere.</p>
            <button type="button" className="btn primary" onClick={() => navigate('/login')}>Sign in</button>
          </>
        ) : (
          <>
            <div className="field">
              <label htmlFor="rp-pw">New password</label>
              <input id="rp-pw" name="password" type="password" autoComplete="new-password" required />
              {s.fieldErrors.password && <span className="field-error">{s.fieldErrors.password}</span>}
              <span className="hint">At least 8 characters: any letters, numbers or symbols you like.</span>
            </div>
            {s.error && <p className="form-error" role="alert">{s.error}</p>}
            <button className="btn primary" disabled={s.busy}>Change password</button>
          </>
        )}
      </form>
    </main>
  );
}
